import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { StatistikDaekning } from '../../models/StatistikDaekning';
import { IndexCard, User } from '../../../auth/store/models';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StatistikDaekningKundeModal } from '../statistik-daekning-kunde-modal/statistik-daekning-kunde-modal';
import { ExcelService, ScookieService } from '@store';
import { Observable } from 'rxjs';
import { StatistikService } from '../../statistik.service';
import { firstDayNextMonthLastYear, lastDayThisMonth } from '../../../shared/month-picker/month-picker.helper';
import { saveAs } from 'file-saver';
import { InvoicesComponent } from '../../../invoices/invoices.component';

@Component({
    selector: 'app-statistik-daekning-ui',
    templateUrl: './statistik.daekning.component.html',
    styleUrls: ['./statistik.daekning.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StatistikDaekningComponent implements OnInit, OnChanges {
    @Input() daekning: StatistikDaekning[];
    @Input() laCards: IndexCard[];
    @Input() kaCards: IndexCard[];
    @Input() saCards: IndexCard[];
    @Input() periode: {fra: string, til: string};
    @Input() user!: User | undefined;
    @Output() reload = new EventEmitter();
    @Output() saChange = new EventEmitter<number>();
    @Output() periodeChange = new EventEmitter<{fra: string, til: string}>();
    filteredDaekning: StatistikDaekning[];
    koncernList: (string | undefined)[] = [];
    showNullableObjects = false;

    selectedLAId: number;
    selectedKAId: number;
    selecetKoncern: string | undefined;

    cookieName = 'statistikDaekningDatoer';

    constructor(
        private router: Router,
        private modalService: NgbModal,
        private statistikService: StatistikService,
        private scookieService: ScookieService,
        private excel: ExcelService,
        private ngbModal: NgbModal,
    ) { }

    ngOnInit() {
        this.scookieService.getCookie(this.cookieName, 1).subscribe(
            data => {
                if (data) {
                    this.onPeriodeChange(data);
                } else {
                    const newPeriode = {
                        fra: firstDayNextMonthLastYear(),
                        til: lastDayThisMonth()
                    };
                    this.onPeriodeChange(newPeriode);
                }
            }
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.user && this.user) {
            this.selectedLAId = this.user.indexCard.ledelsesansvarlig
                ? this.user.indexCard.cardId : undefined;
        }
        if (changes.user && this.user) {
            this.selectedKAId = this.user.indexCard.kundeansvarlig && !this.user.indexCard.ledelsesansvarlig
                ? this.user.indexCard.cardId : undefined;
        }
        if (changes.daekning && !changes.daekning.firstChange) {
            this.sortDaekning();
            this.filteredDaekning = this.daekning;
            this.filterStatistikDaekning();
        }
        this.koncernList = [];
        this.daekning?.forEach(daekning => {
            if (daekning.koncern && this.koncernList.includes(daekning.koncern) === false) {
                this.koncernList.push(daekning.koncern);
            }
        });
    }

    async sortDaekning() {
        this.daekning?.sort((a, b) => a.glKundeNr - b.glKundeNr);
    }

    onPeriodeChange(periode: {fra: string; til: string}) {
        this.filteredDaekning = [];
        this.daekning = [];
        this.periodeChange.emit(periode);
        this.saveDatesToCookie(periode);
    }

    onShowNullableObjectsChange(showNullableObjects: boolean) {
        this.showNullableObjects = showNullableObjects;
        this.filterStatistikDaekning();
    }

    onLedelseAnsvarligSelected($event: any) {
        this.selectedLAId = $event;
        this.filterStatistikDaekning();
    }

    onKundeAnsvarligSelected($event: number) {
        this.selectedKAId = $event;
        this.filterStatistikDaekning();
    }

    onSagsAnsvarligSelected($event: number) {
        this.saChange.emit($event);
    }

    onKoncernSelected(koncern: string) {
        this.selecetKoncern = koncern;
        this.filterStatistikDaekning();
    }

    private filterStatistikDaekning() {
        const filteredKunderByKA = !this.selectedKAId ? this.daekning : this.daekning.filter(kunde => kunde.kaid === this.selectedKAId);
        const filteredKunderByKAAndLA = !this.selectedLAId
            ? filteredKunderByKA
            : filteredKunderByKA.filter(kunde => kunde.laid === this.selectedLAId);
        const filteredKunderByKAAndLAAndKoncern = !this.selecetKoncern
            ? filteredKunderByKAAndLA
            : filteredKunderByKAAndLA.filter(kunde => kunde.koncern === this.selecetKoncern);
        this.filteredDaekning = filteredKunderByKAAndLAAndKoncern;
    }

    calculateDaekningsGrad(brugt: number, faktureret: number): number {
        return 100 * (faktureret - brugt) / brugt;
    }

    navigateToClientBasis(customerId: number) {
        this.router.navigate(['clients', customerId, 'basis']);
        this.statistikService.setVisible(false);
    }

    async openFaktureringModal(glkundenr: number) {
        const fakturaModal = this.ngbModal.open(InvoicesComponent, {size: 'xl'});
        fakturaModal.componentInstance.searchFor = glkundenr.toString();
        await fakturaModal.result
            .then(() => this.reload.emit())
            .catch(() => this.reload.emit());
    }

    async OpenKundeModal(kunde: StatistikDaekning) {
        try {
            const observableData: Observable<any> = this.statistikService.getDaekningKunde(this.periode, kunde.kid);

            const subscription = observableData.subscribe({
                next: (data: any) => {
                    const sagModal = this.modalService.open(StatistikDaekningKundeModal, {size: 'lg'});
                    sagModal.componentInstance.sager = data;
                    sagModal.componentInstance.kunde = kunde;
                    sagModal.componentInstance.periode = this.periode;

                    sagModal.result.finally(() => {
                        subscription.unsubscribe();
                    });
                },
                error: (error: any) => {
                    console.error('Error fetching data:', error);
                }
            });
        } catch (error) {
            console.error('An error occurred:', error);
        }
    }

    trackDaekningKID(_, kunde: StatistikDaekning) {
        return kunde.kid;
    }

    saveDatesToCookie(periode: {fra: string; til: string}): void {
        const cookieValue = JSON.stringify(periode);
        this.scookieService.saveCookieUnique(this.cookieName, cookieValue).subscribe();
    }

    brugtTotal() {
        let total = 0;
        this.filteredDaekning?.forEach(daekning => {
            total += daekning.brugt;
        });
        return Math.round(total);
    }

    faktureretTotal() {
        let total = 0;
        this.filteredDaekning?.forEach(daekning => {
            total += daekning.faktureret;
        });
        return Math.round(total);
    }

    daekningsgradTotal() {
        let total = 0;
        this.filteredDaekning?.forEach(daekning => {
            total += (daekning.faktureret - daekning.brugt);
        });
        return Math.round(total);
    }

    onExport() {
        const formatPercentage = (value: number): number | string =>
            Number.isFinite(value) ? Math.round(value) : 'N/A';

        const exportArray = [
            ['Kundenr.', 'Kunde', 'Brugt', 'Faktureret', 'Dækning', '%'],
            ...this.filteredDaekning.map(daekning => [
                daekning.glKundeNr,
                daekning.navn,
                daekning.brugt,
                daekning.faktureret,
                daekning.faktureret - daekning.brugt,
                formatPercentage(this.calculateDaekningsGrad(daekning.brugt, daekning.faktureret)),
            ])
        ];
        this.excel.generateExcel(exportArray).subscribe((blob: Blob) => {
            saveAs(blob, 'statistik-daekning.xlsx');
        });
    }
}
